/**
 * RedshiftWorkgroupStep.jsx
 *
 * Dedicated step for Amazon Redshift Serverless to select which workgroup
 * compute endpoint to connect and scope ingestion to.
 * Isolated completely from shared/generic wizard steps.
 */
import React from "react";
import { Loader2, XCircle, Database, CheckCircle2 } from "lucide-react";

export function RedshiftWorkgroupStep({ ctx }) {
    const {
        accountsLoading,
        accountsError,
        accounts,
        selectedAccountIds,
        setSelectedAccountIds,
    } = ctx;

    const selectedId = selectedAccountIds[0] || (accounts[0]?.id || "");

    return (
        <div data-testid="wiz-step-redshift-workgroups" className="space-y-4">
            <div>
                <div className="eyebrow flex items-center gap-1.5">
                    <Database className="h-3.5 w-3.5 text-primary" />
                    Select Workgroup to Sync
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    Choose an Amazon Redshift Serverless workgroup. In Redshift, each connector instance connects to one workgroup compute endpoint to query tables and ingest data.
                </div>
            </div>

            {accountsLoading && (
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground py-4" data-testid="workgroups-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Discovering Redshift Serverless workgroups…
                </div>
            )}

            {!accountsLoading && accountsError && (
                <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-3 text-[12px]" data-testid="workgroups-error">
                    <div className="font-medium text-destructive mb-1 flex items-center gap-1.5">
                        <XCircle className="h-3.5 w-3.5" />
                        Could not list workgroups
                    </div>
                    <div className="text-muted-foreground">{accountsError}</div>
                    <div className="text-muted-foreground mt-2">
                        You can still proceed using the host configured in Step 1 — just press Next.
                    </div>
                </div>
            )}

            {!accountsLoading && !accountsError && accounts.length === 0 && (
                <div className="text-[12px] text-muted-foreground py-4" data-testid="workgroups-empty">
                    No additional workgroups discovered. Continuing will use the active workgroup host from Step 1.
                </div>
            )}

            {!accountsLoading && accounts.length > 0 && (
                <div className="space-y-1.5" data-testid="workgroups-list">
                    <div className="grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <div className="col-span-1"></div>
                        <div className="col-span-4">Workgroup Name</div>
                        <div className="col-span-4">Label / Role</div>
                        <div className="col-span-2">Region</div>
                        <div className="col-span-1">Status</div>
                    </div>
                    {accounts.map((a) => {
                        const id = a.id || a.workgroup_name || a.name;
                        const isSelected = selectedAccountIds.includes(id) || (!selectedAccountIds.length && id === accounts[0]?.id);
                        return (
                            <label
                                key={id}
                                className={`grid grid-cols-12 gap-2 items-center px-2 py-2 border rounded-sm cursor-pointer transition-colors ${
                                    isSelected
                                        ? "border-primary bg-primary/5 shadow-xs"
                                        : "border-border hover:bg-muted/30"
                                }`}
                                data-testid={`workgroup-row-${id}`}
                            >
                                <div className="col-span-1 flex items-center justify-center">
                                    <input
                                        type="radio"
                                        name="redshift-workgroup-selection"
                                        checked={isSelected}
                                        onChange={() => setSelectedAccountIds([id])}
                                        className="h-3.5 w-3.5 text-primary focus:ring-primary cursor-pointer"
                                        data-testid={`workgroup-radio-${id}`}
                                    />
                                </div>
                                <div className="col-span-4 font-mono text-[11px] font-medium truncate">{id}</div>
                                <div className="col-span-4 text-[12px] text-muted-foreground truncate" title={a.name}>
                                    {a.name || "—"}
                                </div>
                                <div className="col-span-2 text-[11px] font-mono text-muted-foreground">
                                    {a.currency || a.region || "ap-south-1"}
                                </div>
                                <div className="col-span-1">
                                    <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-emerald-600 font-medium">
                                        {a.status || "AVAILABLE"}
                                    </span>
                                </div>
                            </label>
                        );
                    })}
                    <div className="text-[10px] text-muted-foreground mt-2">
                        Active target: <span className="font-mono text-foreground">{selectedId}</span>
                        {" "}· To sync another workgroup, you can create a second Amazon Redshift connector instance in Analyzio.
                    </div>
                </div>
            )}
        </div>
    );
}

export default RedshiftWorkgroupStep;
